import React, { ReactNode, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { IoClose } from 'react-icons/io5';
import theme from '@styles/theme';
import Loader from '@/shared/components/molecules/Loader';
import { createPortal } from 'react-dom';

type ModalContainerProps = {
	isFixed?: boolean;
	isLoading?: boolean;
	maxWidth?: number;
	title?: string;
	onClose?: () => void;
	children: ReactNode;
}

const ModalContainer = ({
	isFixed,
	isLoading,
	maxWidth,
	title,
	onClose,
	children
}: ModalContainerProps) => {
	const modalElement = document.getElementById('modal') as HTMLElement;
	const bgRef = useRef<HTMLDivElement>(null);
	const modalRef = useRef<HTMLDivElement>(null);
	
	useEffect(() => {
		// 핸들러를 effect 안에 둔다. 밖에 두고 deps 를 [] 로 하면 리스너가 첫 렌더의
		// onClose/isFixed 클로저를 영구히 붙잡아, 나중에 값이 바뀌어도 반영되지 않는다.
		const handleClickOutside = (e: MouseEvent) => {
			if (
				!isFixed &&
				onClose &&
				bgRef?.current?.contains(e.target as HTMLDivElement) &&
				!modalRef?.current?.contains(e.target as HTMLDivElement)
			) {
				onClose();
			}
		};

		document.addEventListener('click', handleClickOutside);
		return () => document.removeEventListener('click', handleClickOutside);
	}, [isFixed, onClose]);
	
	return createPortal(
		<Overlay onClick={onClose}>
			{isLoading && <Loader disabled={true} />}
			<Container
				$maxWidth={maxWidth}
				onClick={(e) => e.stopPropagation()}>
			    <Header>
					{title && <Title>{title}</Title>}
				    {!isFixed && (
					    <CloseButton onClick={onClose}>
	                        <IoClose fontSize={26} color={theme.colors.lightGrayFontColor} />
	                    </CloseButton>
				    )}
			    </Header>
				<Contents>
					{children}
				</Contents>
			</Container>
		</Overlay>
		, modalElement);
};

export default ModalContainer;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
`;
const Container = styled.div<{ $maxWidth?: number; }>`
  min-width: 452px;
  max-width: ${({ $maxWidth }) => $maxWidth ? $maxWidth : 1080}px;
  padding: 32px;
  border-radius: 4px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  background-color: ${theme.colors.whiteColor};
`;
const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const CloseButton = styled.button`
  cursor: pointer;

  &:hover svg {
    transition: .25s;
    fill: ${theme.colors.blackColor};
  }
`;
const Title = styled.h3`
  ${theme.typography.subTitle};
  font-family: PretendardSemiBold, AppleSDGothicNeoSemiBold, sans-serif;
  font-weight: 600;
  color: ${theme.colors.blackColor};
`;
const Contents = styled.div`
  margin-top: 28px;
`;
export const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 32px;
`;
